import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import { fetchProducts, mapShopifyProduct } from "@/lib/shopify";

export default function FreshPicks() {
  const [products, setProducts] = useState([]);
  const scrollRef = useRef(null);
  const { addItem } = useCart();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const result = await fetchProducts(12);
      if (!cancelled && result.products?.length > 0) {
        setProducts(result.products.map(mapShopifyProduct));
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.offsetWidth * 0.7;
    scrollRef.current.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  const handleAdd = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
  };

  if (products.length === 0) return null;

  return (
    <section data-testid="section-fresh-picks" className="py-16 lg:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex items-end justify-between mb-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber mb-2">
              Just landed
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-plum tracking-tight">
              Fresh Picks
            </h2>
          </motion.div>

          {/* Scroll arrows — desktop */}
          <div className="hidden sm:flex items-center gap-2">
            <button
              data-testid="fresh-picks-scroll-left"
              onClick={() => scroll("left")}
              className="w-10 h-10 rounded-full border border-plum/15 flex items-center justify-center text-plum hover:bg-plum hover:text-white transition-colors duration-200"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              data-testid="fresh-picks-scroll-right"
              onClick={() => scroll("right")}
              className="w-10 h-10 rounded-full border border-plum/15 flex items-center justify-center text-plum hover:bg-plum hover:text-white transition-colors duration-200"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable track */}
      <div
        ref={scrollRef}
        data-testid="fresh-picks-carousel"
        className="flex gap-5 lg:gap-6 overflow-x-auto scrollbar-hide px-6 lg:px-10 pb-4 snap-x snap-mandatory"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
      >
        {/* Left spacer for max-w-7xl alignment */}
        <div className="shrink-0 w-0 lg:w-[calc((100vw-80rem)/2)]" />

        {products.map((product, i) => (
          <motion.div
            key={product.id}
            data-testid={`fresh-pick-card-${i}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ delay: i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="shrink-0 w-[260px] sm:w-[280px] lg:w-[300px] snap-start"
          >
            <Link to={`/product/${product.handle}`} className="group block">
              {/* Image */}
              <div className="relative rounded-2xl overflow-hidden mb-3">
                <div className="aspect-[3/4]" style={{ backgroundColor: product.color || "#FDEAEB" }}>
                  {product.image && (
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  )}
                </div>

                {/* Checked badge */}
                <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-plum text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                  <Check className="w-3 h-3" />
                  <span>Checked for you</span>
                </div>

                {/* Quick add */}
                <div className="absolute inset-x-0 bottom-0 p-3 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  <button
                    onClick={(e) => handleAdd(e, product)}
                    className="w-full bg-white/95 backdrop-blur text-plum font-semibold text-sm py-2.5 rounded-xl hover:bg-white transition-colors duration-200"
                  >
                    Add to bag
                  </button>
                </div>
              </div>

              {/* Info */}
              <div className="space-y-1 px-0.5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-plum-light">
                  {product.category}
                </p>
                <h3 className="font-serif text-base font-semibold text-plum leading-snug line-clamp-1">
                  {product.title}
                </h3>
                <div className="flex items-center gap-2.5">
                  {product.fit && (
                    <Badge className="bg-amber/15 text-amber border-0 text-xs font-semibold rounded-full px-2.5 py-0.5">
                      {product.fit}
                    </Badge>
                  )}
                  <span className="text-sm font-semibold text-plum">
                    ${product.price.toLocaleString()}
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}

        {/* Right spacer */}
        <div className="shrink-0 w-6 lg:w-[calc((100vw-80rem)/2)]" />
      </div>
    </section>
  );
}
